import { FormControl, MenuItem, Select, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useBusiness } from '../../contexts/BusinessContext';
import { useSession } from '../../contexts/SessionContext';

export default function ContextSelector() {
  const { access } = useAuth();
  const {
    tenants,
    activeTenantId,
    activeEntityId,
    activeConfigId,
    setActiveTenantId,
    setActiveEntityId,
    setActiveConfigIdSafe,
  } = useBusiness();
  const { session, formSessionOverride } = useSession();

  const activeTenant = useMemo(
    () => tenants.find((t) => t.tenant_id === activeTenantId) ?? null,
    [tenants, activeTenantId],
  );
  const entities = activeTenant?.entities ?? [];
  const activeEntity = entities.find((e) => e.entity_id === activeEntityId) ?? null;
  // Use configs from /api/auth/me only. Do not intersect with availableWebAdapterConfigs:
  // that list is derived from schema_source/config_type and would hide valid entity-linked
  // adapters (e.g. second config under the same entity) from the header selector.
  const configs = activeEntity?.configs ?? [];
  const lockBaseConfigSwitch = Boolean(session || formSessionOverride);

  const lockSingleConfig = Boolean(
    access?.config_scope_restricted && access.allowed_config_ids?.length === 1,
  );

  if (lockSingleConfig) {
    const tenantLabel = activeTenant?.name ?? activeTenantId ?? '—';
    const entityLabel = activeEntity?.name ?? '—';
    const cfgLabel =
      configs.find((c) => c.config_id === activeConfigId)?.name ?? activeConfigId ?? '—';
    return (
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mr: { xs: 0, md: 1.5 }, minWidth: 0, flex: '0 1 auto', maxWidth: '100%' }}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'white',
            opacity: 0.9,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={`${tenantLabel} · ${entityLabel} · ${cfgLabel}`}
        >
          Context: {tenantLabel} · {entityLabel} · {cfgLabel}
        </Typography>
      </Stack>
    );
  }

  const selectPaperSx = {
    color: 'text.primary',
    '& .MuiMenuItem-root': { fontSize: '0.875rem' },
  };

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      alignItems={{ xs: 'stretch', sm: 'center' }}
      sx={{
        mr: { xs: 0, md: 1.5 },
        minWidth: 0,
        flex: { xs: '1 1 100%', sm: '0 1 auto' },
        maxWidth: '100%',
        width: { xs: '100%', sm: 'auto' },
        alignSelf: { xs: 'stretch', sm: 'center' },
      }}
    >
      <Typography variant="caption" sx={{ color: 'white', opacity: 0.9, display: { xs: 'none', md: 'block' } }}>
        Context
      </Typography>

      <FormControl
        size="small"
        sx={{
          flex: { xs: '1 1 auto', sm: '0 1 auto' },
          minWidth: { xs: 0, sm: 120 },
          maxWidth: { xs: '100%', sm: 200, md: 220 },
          bgcolor: 'rgba(255,255,255,0.12)',
          borderRadius: 1,
        }}
      >
        <Select
          value={activeTenantId ?? ''}
          onChange={(e) => {
            const tid = String(e.target.value || '');
            setActiveTenantId(tid || null);
            // reset entity/config; BusinessContext refresh will auto-resolve
            setActiveEntityId(null);
            setActiveConfigIdSafe(null, lockBaseConfigSwitch);
          }}
          displayEmpty
          MenuProps={{ PaperProps: { sx: selectPaperSx } }}
          sx={{
            color: 'white',
            '& .MuiSvgIcon-root': { color: 'white' },
            '& .MuiSelect-select': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              pr: '32px !important',
            },
          }}
        >
          {tenants.length === 0 ? (
            <MenuItem value="">
              No tenant
            </MenuItem>
          ) : (
            tenants.map((t) => (
              <MenuItem key={t.tenant_id} value={t.tenant_id}>
                {t.name || t.tenant_id}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>

      <FormControl
        size="small"
        sx={{
          flex: { xs: '1 1 auto', sm: '0 1 auto' },
          minWidth: { xs: 0, sm: 120 },
          maxWidth: { xs: '100%', sm: 220, md: 260 },
          bgcolor: 'rgba(255,255,255,0.12)',
          borderRadius: 1,
        }}
      >
        <Select
          value={activeEntityId ?? ''}
          onChange={(e) => {
            const eid = String(e.target.value || '');
            setActiveEntityId(eid || null);
            setActiveConfigIdSafe(null, lockBaseConfigSwitch);
          }}
          displayEmpty
          MenuProps={{ PaperProps: { sx: selectPaperSx } }}
          sx={{
            color: 'white',
            '& .MuiSvgIcon-root': { color: 'white' },
            '& .MuiSelect-select': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              pr: '32px !important',
            },
          }}
        >
          {entities.length === 0 ? (
            <MenuItem value="">
              No entity
            </MenuItem>
          ) : (
            entities.map((e) => (
              <MenuItem key={e.entity_id} value={e.entity_id}>
                {e.name}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>

      <FormControl
        size="small"
        sx={{
          flex: { xs: '1 1 auto', sm: '0 1 auto' },
          minWidth: { xs: 0, sm: 120 },
          maxWidth: { xs: '100%', sm: 200, md: 220 },
          bgcolor: 'rgba(255,255,255,0.12)',
          borderRadius: 1,
        }}
      >
        <Select
          value={activeConfigId ?? ''}
          onChange={(e) => {
            const next = String(e.target.value || '') || null;
            setActiveConfigIdSafe(next, lockBaseConfigSwitch);
          }}
          displayEmpty
          disabled={lockBaseConfigSwitch}
          MenuProps={{ PaperProps: { sx: selectPaperSx } }}
          sx={{
            color: 'white',
            '& .MuiSvgIcon-root': { color: 'white' },
            '& .MuiSelect-select': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              pr: '32px !important',
            },
          }}
        >
          {configs.length === 0 ? (
            <MenuItem value="">
              (All forms)
            </MenuItem>
          ) : (
            configs.map((c) => (
              <MenuItem key={c.config_id} value={c.config_id}>
                {c.name || c.config_id}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
      {lockBaseConfigSwitch && (
        <Typography variant="caption" sx={{ color: 'white', opacity: 0.85, display: { xs: 'none', lg: 'block' } }}>
          Finish current session before switching adapter
        </Typography>
      )}
    </Stack>
  );
}

